import React, { useEffect, useState } from "react";
import { db } from "../../db/firebaseConfig";
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

const formatPrice = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0, // sin decimales (ej: $6.200)
  }).format(value);
};

export default function StockActual() {
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState("Todas");

  // 🔹 Para modal de edición
  const [productoEditando, setProductoEditando] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    price: "",
    stock: "",
    img: "",
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "producto"), (snapshot) => {
      const lista = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProductos(lista);
    });

    return () => unsubscribe();
  }, []);

  const categorias = ["Todas", ...new Set(productos.map((p) => p.category))];

  // 🔍 Filtrado
  const productosFiltrados = productos.filter((p) => {
    const coincideNombre = p.name.toLowerCase().includes(busqueda.toLowerCase());
    const coincideCategoria =
      categoriaSeleccionada === "Todas" || p.category === categoriaSeleccionada;

    return coincideNombre && coincideCategoria;
  });

  // ➕➖ Modificar stock
  const modificarStock = async (id, cantidad) => {
    const productoRef = doc(db, "producto", id);
    const prodActual = productos.find((p) => p.id === id);
    const nuevoStock = Math.max(0, prodActual.stock + cantidad); // nunca negativo

    await updateDoc(productoRef, { stock: nuevoStock });
  };

  // 🗑️ Eliminar producto
  const eliminarProducto = async (id) => {
    if (window.confirm("¿Seguro que querés eliminar este producto?")) {
      await deleteDoc(doc(db, "producto", id));
    }
  };

  // ✏️ Abrir modal edición
  const abrirModalEdicion = (producto) => {
    setProductoEditando(producto.id);
    setFormData({
      name: producto.name,
      category: producto.category,
      price: producto.price,
      stock: producto.stock,
      img: producto.img,
    });
  };

  // ✏️ Guardar cambios
  const guardarEdicion = async () => {
    const productoRef = doc(db, "producto", productoEditando);
    await updateDoc(productoRef, {
      ...formData,
      price: parseFloat(formData.price),
      stock: parseInt(formData.stock),
    });

    setProductoEditando(null); // cerrar modal
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-2xl font-bold mb-4">📦 Stock Actual</h3>

      {/* 🔎 Barra de búsqueda y filtro */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Buscar producto..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="border p-2 rounded w-full md:w-1/2"
        />

        <select
          value={categoriaSeleccionada}
          onChange={(e) => setCategoriaSeleccionada(e.target.value)}
          className="border p-2 rounded w-full md:w-1/4"
        >
          {categorias.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* 📋 Tabla de productos */}
      {productosFiltrados.length === 0 ? (
        <p className="text-gray-500">No se encontraron productos.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">Imagen</th>
                <th className="p-2 border">Nombre</th>
                <th className="p-2 border">Categoría</th>
                <th className="p-2 border">Precio</th>
                <th className="p-2 border">Stock</th>
                <th className="p-2 border">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {productosFiltrados.map((prod) => (
                <tr key={prod.id} className="text-center">
                  <td className="p-2 border">
                    {prod.img ? (
                      <img
                        src={prod.img}
                        alt={prod.name}
                        className="w-16 h-16 object-cover mx-auto rounded"
                      />
                    ) : (
                      "❌"
                    )}
                  </td>
                  <td className="p-2 border">{prod.name}</td>
                  <td className="p-2 border">{prod.category}</td>
                  <td className="p-2 border">{formatPrice(prod.price)}</td>
                  <td className="p-2 border">{prod.stock}</td>
                  <td className="p-2 border flex gap-2 justify-center">
                    {/* ➕➖ botones stock */}
                    <button
                      onClick={() => modificarStock(prod.id, +1)}
                      className="bg-green-500 text-white px-2 py-1 rounded"
                    >
                      +1
                    </button>
                    <button
                      onClick={() => modificarStock(prod.id, -1)}
                      className="bg-yellow-500 text-white px-2 py-1 rounded"
                    >
                      -1
                    </button>

                    {/* 🗑️ Eliminar */}
                    <button
                      onClick={() => eliminarProducto(prod.id)}
                      className="bg-red-600 text-white px-2 py-1 rounded"
                    >
                      Borrar
                    </button>

                    {/* ✏️ Editar */}
                    <button
                      onClick={() => abrirModalEdicion(prod)}
                      className="bg-blue-600 text-white px-2 py-1 rounded"
                    >
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 🟦 Modal edición */}
      {productoEditando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-xl font-bold mb-4">Editar Producto</h3>

            <input
              type="text"
              placeholder="Nombre"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              type="text"
              placeholder="Categoría"
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              type="number"
              placeholder="Precio"
              value={formData.price}
              onChange={(e) =>
                setFormData({ ...formData, price: e.target.value })
              }
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              type="number"
              placeholder="Stock"
              value={formData.stock}
              onChange={(e) =>
                setFormData({ ...formData, stock: e.target.value })
              }
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              type="text"
              placeholder="URL Imagen"
              value={formData.img}
              onChange={(e) => setFormData({ ...formData, img: e.target.value })}
              className="border p-2 w-full mb-4 rounded"
            />

            <div className="flex justify-between">
              <button
                onClick={guardarEdicion}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Guardar
              </button>
              <button
                onClick={() => setProductoEditando(null)}
                className="bg-gray-400 text-white px-4 py-2 rounded"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
